/**
 * NoteCell - the sticky note for this lead on this study, typed straight into the row
 * (docs/plans/crm-recruitment-list-plan.md Part A sections 3 step 6 and 6, decision 4, B2
 * saveRecruitNote, B4).
 *
 * Click (or Enter from the sheet's keyboard) opens a small textarea. Enter or clicking away saves;
 * Shift+Enter is a new line; Esc cancels and keeps the note as it was. The save sends the note's
 * `recruit_note_at` from when the editor opened (`expected_at`; null when there was no note), so a
 * teammate's change meanwhile comes back as the 409 note_changed: the cell then shows their note,
 * "Edited by Dana just now", and two plain choices, Overwrite (send mine over theirs) or Keep
 * theirs. The server keeps every version on the lead's timeline; the cell only ever shows the
 * current one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { relTime } from '../../../lib';
import { contactName } from '../../../types';
import { noteConflictOf, recruitErrorMessage, saveRecruitNote } from '../../api';
import { RECRUIT_NOTE_MAX, noteEditedLine } from '../../recruitLogic';
import type { RecruitNoteResponse, RecruitRow, RecruitRowChangeHandler, RecruitStudy } from '../../types';

export interface NoteCellProps {
  row: RecruitRow;
  /** The study the list is for (the note route's :studyId). */
  study: Pick<RecruitStudy, 'id'>;
  onRowChange: RecruitRowChangeHandler;
  /** Sheet keyboard: Enter opened this cell (open the editor). */
  editing?: boolean;
  /** The edit finished (saved, or cancelled with Esc). */
  onEditEnd?: () => void;
  /** The editor opened or closed, however it happened (the sheet keeps a refresh off the draft). */
  onEditingChange?: (open: boolean) => void;
  className?: string;
}

/** The row's note fields from a save's (or a 409's) answer. */
function withNote(row: RecruitRow, note: RecruitNoteResponse): RecruitRow {
  return {
    ...row,
    membership: {
      ...row.membership,
      recruit_note: note.note,
      recruit_note_at: note.at,
      recruit_note_by: note.by,
      recruit_note_by_name: note.by_name,
    },
  };
}

const smallBtnCls =
  'inline-flex min-h-[44px] items-center rounded-md border px-2.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:h-7 sm:min-h-0';

export function NoteCell({
  row,
  study,
  onRowChange,
  editing = false,
  onEditEnd,
  onEditingChange,
  className = '',
}: NoteCellProps) {
  const contactId = row.contact.id;
  const studyId = study.id;
  const name = contactName(row.contact);
  const note = row.membership.recruit_note || '';

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A 409: the teammate's note as it is now, and the text this recruiter tried to save.
  const [conflict, setConflict] = useState<RecruitNoteResponse | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const openRef = useRef(false);
  openRef.current = open;
  const savingRef = useRef(false);
  // Esc sets this so the blur that follows the cancel saves nothing.
  const cancelledRef = useRef(false);
  // The note and its timestamp when the editor opened: what "changed" and `expected_at` mean.
  const baseNoteRef = useRef('');
  const baseAtRef = useRef<string | null>(null);
  const rowRef = useRef(row);
  rowRef.current = row;
  const onEditEndRef = useRef(onEditEnd);
  onEditEndRef.current = onEditEnd;
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;

  const begin = useCallback(() => {
    const m = rowRef.current.membership;
    baseNoteRef.current = m.recruit_note || '';
    baseAtRef.current = m.recruit_note_at ?? null;
    cancelledRef.current = false;
    setDraft(m.recruit_note || '');
    setError(null);
    // Editing again settles an earlier conflict: this edit starts from the note as it is now.
    setConflict(null);
    setPending(null);
    setOpen(true);
  }, []);

  // Enter from the sheet opens the editor (never twice: an open draft is left alone).
  useEffect(() => {
    if (editing && !openRef.current) begin();
  }, [editing, begin]);

  // Report every open and close, but not the first render (it is closed then anyway).
  const reportedRef = useRef(false);
  useEffect(() => {
    if (reportedRef.current === open) return;
    reportedRef.current = open;
    onEditingChangeRef.current?.(open);
  }, [open]);

  const persist = useCallback(async (text: string, expectedAt: string | null | undefined) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const saved = await saveRecruitNote(studyId, contactId, text, expectedAt);
      onRowChange(contactId, (r) => withNote(r, saved));
      setConflict(null);
      setPending(null);
      setOpen(false);
      onEditEndRef.current?.();
    } catch (e: unknown) {
      const clash = noteConflictOf(e);
      if (clash) {
        // Show the note as it is now, with who changed it, and let the recruiter decide.
        onRowChange(contactId, (r) => withNote(r, clash.current));
        setConflict(clash.current);
        setPending(text);
        setOpen(false);
        onEditEndRef.current?.();
      } else {
        setError(recruitErrorMessage(e, "The note wasn't saved. Please try again."));
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [studyId, contactId, onRowChange]);

  const save = useCallback(() => {
    // Only an open editor saves: a late blur after it closed must never send a second write.
    if (!openRef.current || savingRef.current) return;
    if (draft.trim() === baseNoteRef.current.trim()) {
      // Nothing changed: close without a write (and without a timeline line).
      setOpen(false);
      setError(null);
      onEditEndRef.current?.();
      return;
    }
    void persist(draft, baseAtRef.current);
  }, [draft, persist]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    setOpen(false);
    setError(null);
    onEditEndRef.current?.();
  }, []);

  const overwrite = () => {
    if (pending === null || !conflict) return;
    void persist(pending, conflict.at ?? null);
  };

  const keepTheirs = () => {
    setConflict(null);
    setPending(null);
  };

  if (open) {
    return (
      <div className={`min-w-0 ${className}`}>
        <textarea
          autoFocus
          value={draft}
          readOnly={saving}
          maxLength={RECRUIT_NOTE_MAX}
          rows={3}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              e.stopPropagation();
              save();
              return;
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              cancel();
            }
          }}
          onBlur={() => {
            if (cancelledRef.current) {
              cancelledRef.current = false;
              return;
            }
            save();
          }}
          aria-label={`Note for ${name} on this study`}
          placeholder="Type a note…"
          className="block w-full resize-y rounded-md border border-primary-500 bg-white px-1.5 py-1 text-xs text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-900 dark:text-gray-100"
        />
        <div className="mt-0.5 flex items-center justify-between gap-2 text-[10px] text-gray-400 dark:text-gray-500">
          <span>{saving ? 'Saving…' : 'Enter saves · Esc cancels'}</span>
          <span className="tabular-nums">{draft.length.toLocaleString()}/{RECRUIT_NOTE_MAX.toLocaleString()}</span>
        </div>
        {error && <p role="alert" className="mt-0.5 text-[11px] leading-snug text-red-600 dark:text-red-400">{error}</p>}
      </div>
    );
  }

  const byLine = row.membership.recruit_note_by_name
    ? `${row.membership.recruit_note_by_name}, ${relTime(row.membership.recruit_note_at)}`
    : row.membership.recruit_note_at ? relTime(row.membership.recruit_note_at) : '';

  return (
    <div className={`min-w-0 ${className}`}>
      <button
        type="button"
        onClick={begin}
        title={note ? (byLine ? `${note}\n\n${byLine}` : note) : 'Add a note for this lead on this study'}
        aria-label={note ? `Edit the note for ${name}` : `Add a note for ${name}`}
        className="block min-h-[44px] w-full truncate rounded-md px-1.5 text-left text-xs transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700/60 sm:min-h-0 sm:py-1"
      >
        {note
          ? <span className="text-gray-800 dark:text-gray-200">{note}</span>
          : <span className="text-gray-400 dark:text-gray-500">Add note…</span>}
      </button>
      {conflict && (
        <div
          role="alert"
          className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-1 text-[11px] leading-snug text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <p>{noteEditedLine(conflict)}</p>
          {pending !== null && (
            <p className="truncate" title={pending || undefined}>Yours: {pending.trim() ? pending : '(empty)'}</p>
          )}
          <div className="mt-1 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={overwrite}
              disabled={saving}
              className={`${smallBtnCls} border-amber-300 bg-white text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-gray-800 dark:text-amber-300 dark:hover:bg-amber-900/30`}
            >
              Overwrite
            </button>
            <button
              type="button"
              onClick={keepTheirs}
              disabled={saving}
              className={`${smallBtnCls} border-transparent text-amber-800 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30`}
            >
              Keep theirs
            </button>
          </div>
        </div>
      )}
      {error && <p role="alert" className="mt-0.5 text-[11px] leading-snug text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

export default NoteCell;
